"use client";

// Deleting a project takes its chats with it, so it always asks first — and
// the question names what goes, because a count is the whole difference
// between "remove a folder" and "lose eleven conversations".
//
// Shared by the sidebar's ⋯ menu (one project) and the projects page (a
// selection), so the wording can't drift between them.

import { TriangleAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { COMMAND_V2_DIALOG_CONTENT_CLASS } from "@/components/command-team-v2/utils/dialog-styles";

import type { ProtoChatSession, ProtoProject } from "../prototype/types";

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

/**
 * What deleting `projects` would destroy, as the sentence the dialog and the
 * toast both use.
 */
export function describeProjectDeletion(
  projects: ProtoProject[],
  sessions: ProtoChatSession[]
) {
  const ids = new Set(projects.map((project) => project.id));
  const chatCount = sessions.filter(
    (session) => session.projectId && ids.has(session.projectId)
  ).length;
  const projectLabel =
    projects.length === 1 ? `“${projects[0].name}”` : plural(projects.length, "project");
  return {
    chatCount,
    projectLabel,
    /** e.g. `“Weekly Reporting” and its 2 chats` */
    summary: chatCount
      ? `${projectLabel} and ${chatCount === 1 ? "its chat" : `its ${chatCount} chats`}`
      : projectLabel,
  };
}

export function DeleteProjectsDialog({
  projects,
  sessions,
  onClose,
  onConfirm,
}: {
  /** null → closed. */
  projects: ProtoProject[] | null;
  sessions: ProtoChatSession[];
  onClose: () => void;
  onConfirm: () => void;
}) {
  const open = projects !== null && projects.length > 0;
  const { chatCount, projectLabel } = describeProjectDeletion(projects ?? [], sessions);

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className={cn(COMMAND_V2_DIALOG_CONTENT_CLASS, "max-w-md")}>
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-red-50 dark:bg-red-950/30">
            <TriangleAlert className="size-4 text-destructive" />
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-left text-lg font-semibold tracking-tight">
              Delete {projects && projects.length === 1 ? "project" : "projects"}?
            </DialogTitle>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {chatCount > 0 ? (
                <>
                  Deleting {projectLabel} also deletes{" "}
                  <span className="font-semibold text-foreground">
                    {chatCount === 1 ? "the 1 chat" : `all ${chatCount} chats`}
                  </span>{" "}
                  inside {projects && projects.length === 1 ? "it" : "them"}. This
                  can&rsquo;t be undone.
                </>
              ) : (
                <>
                  Deleting {projectLabel}. There are no chats inside, so nothing else
                  goes with it.
                </>
              )}
            </p>
          </div>
        </div>

        {chatCount > 0 && projects ? (
          <ul className="mt-4 max-h-40 space-y-1 overflow-y-auto rounded-lg border border-border/60 bg-muted/40 p-2">
            {projects.map((project) => {
              const count = sessions.filter((s) => s.projectId === project.id).length;
              return (
                <li
                  key={project.id}
                  className="flex items-center justify-between gap-3 px-1.5 py-1 text-[13px]"
                >
                  <span className="min-w-0 truncate font-medium">{project.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {count === 0 ? "no chats" : plural(count, "chat")}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : null}

        <div className="mt-6 flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-foreground transition-opacity hover:opacity-70"
          >
            Cancel
          </button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="h-10 rounded-full px-6 text-sm font-medium"
          >
            Delete{chatCount > 0 ? ` ${plural(chatCount, "chat")} too` : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
